import { useState } from 'react';
import IconSvg from 'src/assets/icons/IconSvg';

interface Props {
  size?: 'sm' | 'md';
  className?: string;
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
}

const NumberInputWithMultiplier = ({
  size = 'sm',
  className,
  value: controlledValue,
  onChange,
  min = 1,
  max = 100,
}: Props) => {
  const [internalValue, setInternalValue] = useState<number>(min);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const setValue = (newValue: number) => {
    const clamped = Math.max(min, Math.min(max, newValue));
    if (isControlled) {
      onChange?.(clamped);
    } else {
      setInternalValue(clamped);
    }
  };

  const handleIncrement = () => {
    if (value < max) {
      setValue(value + 1);
    }
  };

  const handleDecrement = () => {
    setValue(value > min ? value - 1 : min);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = Math.max(min, parseInt(event.target.value.replace(/\D/g, ''), 10) || min);
    setValue(inputValue);
  };

  const handleMax = () => {
    setValue(max);
  };

  return (
    <div
      className={`flex gap-2 items-center px-4 rounded-lg bg-grayscale-900 py-1 h-[34px] ${className} ${
        size === 'sm' ? 'xl:h-[42px]' : 'xl:h-[48px]'
      }`}
    >
      <div className="relative w-full">
        <button
          onClick={handleDecrement}
          className="flex justify-center items-center p-0.5 absolute text-grayscale-400 top-1/2 -translate-y-1/2"
        >
          <IconSvg icon="minusS" />
        </button>

        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          className="text-center text-brand-magnolia bg-grayscale-900 !ring-0 !outline-none w-full text-lg font-semibold"
        />

        <button
          onClick={handleIncrement}
          className="flex justify-center items-center p-0.5 text-grayscale-400 absolute top-1/2 -translate-y-1/2 right-0"
        >
          <IconSvg icon="plusS" />
        </button>
      </div>

      {size === 'sm' && (
        <button onClick={handleMax}>
          <svg
            width="34"
            height="18"
            viewBox="0 0 34 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="34" height="18" rx="2" fill="#605F5E" />
            <path
              d="M4.913 13V5.3H6.277L8.697 8.864L11.095 5.3H12.47V13H11.194V7.313L8.686 11.02L6.189 7.335V13H4.913ZM19.5159 13L16.5129 5.3H17.7889L20.8689 13H19.5159ZM13.3669 13L16.4359 5.3H17.7229L14.7199 13H13.3669ZM14.8079 11.108V9.975H19.4169V11.108H14.8079ZM20.9593 13L23.9403 8.776L26.3713 5.3H27.8893L24.9743 9.326L22.4993 13H20.9593ZM26.3823 13L23.9183 9.326L20.9923 5.3H22.5213L24.9523 8.776L27.9223 13H26.3823Z"
              fill="#10100F"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default NumberInputWithMultiplier;
